import { z } from "zod";
import { authoringSlugSchema } from "./authoring-shared.js";
import { twinProductContractSchema } from "./convergence.js";
const idSchema = z.string().trim().min(1).max(160);
const relationSchema = z.string().trim().min(1).max(127);
export const activateTwinProductVersionInputSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
    candidateReleaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u),
    candidateBuildId: z.string().regex(/^twin_cbuild_[0-9a-f]{32}$/u),
    candidateCatalogSha256: z.string().regex(/^[0-9a-f]{64}$/u),
}).strict();
export const activateTwinProductVersionResponseSchema = z.object({
    publicationId: idSchema,
    publicationVersionId: idSchema,
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    implementationRelation: relationSchema,
    publishedRelation: relationSchema,
    previousRelationId: idSchema.nullable(),
    candidateReleaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u),
    candidateBuildId: z.string().regex(/^twin_cbuild_[0-9a-f]{32}$/u),
    candidateCatalogSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    activated: z.literal(true),
}).strict();
export const deactivateTwinProductVersionInputSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
}).strict();
export const deactivateTwinProductVersionResponseSchema = z.object({
    publicationId: idSchema,
    publicationVersionId: idSchema,
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    deactivationId: idSchema,
    activationId: idSchema,
    publishedRelation: relationSchema,
    deactivated: z.literal(true),
}).strict();
export const twinDerivedRelationRetirementInputSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
}).strict();
export const twinDerivedRelationRetirementResponseSchema = z.object({
    derivedRelationId: idSchema,
    slug: z.string().trim().min(1).max(120),
    version: z.number().int().positive(),
    relationSchema,
    relationName: relationSchema,
    status: z.enum(["active", "archived"]),
    action: z.enum(["retire", "restore"]),
    retirementId: idSchema,
}).strict();
export const repairTwinProductWrapperInputSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
}).strict();
export const repairTwinProductWrapperResponseSchema = z.object({
    repairId: idSchema,
    publicationId: idSchema,
    publicationVersionId: idSchema,
    productSlug: authoringSlugSchema,
    productVersion: z.number().int().positive(),
    implementationRelation: relationSchema,
    publishedRelation: relationSchema,
    repairedAt: z.number().int().positive(),
    repaired: z.literal(true),
}).strict();
export const repairTwinProductRoutingInputSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
}).strict();
export const repairTwinProductRoutingResponseSchema = z.object({
    repairId: idSchema,
    publicationId: idSchema,
    publicationVersionId: idSchema,
    productSlug: authoringSlugSchema,
    productVersion: z.number().int().positive(),
    skillSlugs: z.array(authoringSlugSchema).min(1),
    repairedAt: z.number().int().positive(),
    repaired: z.literal(true),
}).strict();
export const repairTwinProductBindingInputSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
}).strict();
export const repairTwinProductBindingResponseSchema = z.object({
    waveId: idSchema,
    receiptId: idSchema,
    publicationId: idSchema,
    publicationVersionId: idSchema,
    productSlug: authoringSlugSchema,
    bindingIds: z.array(idSchema).min(1),
    repairedAt: z.number().int().positive(),
    repaired: z.literal(true),
}).strict();
export const reviseTwinProductVersionInputSchema = z.object({
    organisationId: idSchema,
    contract: twinProductContractSchema,
    skillSlugs: z.array(authoringSlugSchema)
        .min(1)
        .refine((slugs) => new Set(slugs).size === slugs.length, "Skill slugs must be unique")
        .optional(),
    regenerateGuidance: z.boolean().optional(),
    reason: z.string().trim().min(1).max(4000),
}).strict().superRefine((input, context) => {
    if (input.regenerateGuidance && input.skillSlugs !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["skillSlugs"],
            message: "Guidance regeneration uses the current skill snapshot; omit skillSlugs.",
        });
    }
});
export const reviseTwinProductVersionResponseSchema = z.object({
    publicationId: idSchema,
    publicationVersionId: idSchema,
    productSlug: z.string().trim().min(1).max(120),
    productVersion: z.number().int().positive(),
    relationId: idSchema,
    publishedRelation: relationSchema,
    skillVersionIds: z.array(idSchema).min(1),
    activated: z.literal(false),
}).strict();
export const twinRuntimeIdentifierCountsSchema = z.object({
    products: z.number().int().nonnegative(),
    contracts: z.number().int().nonnegative(),
    skillVersions: z.number().int().nonnegative(),
    matches: z.number().int().nonnegative(),
}).strict();
export const twinRuntimeIdentifierOffenderSchema = z.object({
    productSlug: authoringSlugSchema,
    publicationVersionId: idSchema,
    contractMatches: z.number().int().nonnegative(),
    skillVersionMatches: z.number().int().nonnegative(),
    skillVersionIds: z.array(idSchema),
}).strict();
export const twinRuntimeIdentifierInventorySchema = z.object({
    migrationVersion: z.number().int().positive(),
    tenantSlug: idSchema,
    organisationId: idSchema,
    offenders: z.array(twinRuntimeIdentifierOffenderSchema),
    counts: twinRuntimeIdentifierCountsSchema,
    inventorySha256: z.string().regex(/^[0-9a-f]{64}$/u),
}).strict();
export const stageTwinRuntimeIdentifiersInputSchema = z.object({
    expectedInventorySha256: z.string().regex(/^[0-9a-f]{64}$/u),
    releaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u),
    reason: z.string().trim().min(1).max(4000),
}).strict();
export const twinRuntimeIdentifierMigrationReceiptSchema = z.object({
    id: idSchema,
    migrationVersion: z.number().int().positive(),
    tenantSlug: idSchema,
    organisationId: idSchema,
    actorSubject: idSchema,
    reason: z.string().trim().min(1).max(4000),
    releaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u),
    inventorySha256: z.string().regex(/^[0-9a-f]{64}$/u),
    resultSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    offenderCounts: twinRuntimeIdentifierCountsSchema,
    stagedSkillVersionIds: z.array(idSchema),
    stagedPublicationVersionIds: z.array(idSchema),
    createdAt: z.number().int().nonnegative(),
}).strict();
